import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Alert,
    CircularProgress,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    Divider,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    PlayArrow as ExecuteIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    Cloud as CloudIcon,
    Create as CreateIcon,
    Image as ImageIcon,
    Article as ArticleIcon,
    Web as WebIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axiosWithAuth from '../utils/axiosWithAuth';
import ContentGenerator from '../components/ContentGeneration/ContentGenerator';
import ContentStatusAndDownload from '../components/ContentStatusAndDownload/ContentStatusAndDownload';
import { isCASOnlySubscription } from '../utils/subscriptionUtils';

const ServiceOperationsPage = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchParams, setSearchParams] = useSearchParams();
    const [operations, setOperations] = useState([]);
    const [quotaInfo, setQuotaInfo] = useState({});
    const [userRole, setUserRole] = useState('');
    const [userSubscriptions, setUserSubscriptions] = useState([]);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    // Operation execution state
    const [selectedOperation, setSelectedOperation] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [executing, setExecuting] = useState(false);
    const [operationForm, setOperationForm] = useState({});
    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);

    useEffect(() => {
        fetchAvailableOperations();
        fetchUserSubscriptions();
    }, []);

    useEffect(() => {
        // Handle subscriptionId parameter from URL
        const subscriptionId = searchParams.get('subscriptionId');
        if (subscriptionId && userSubscriptions.length > 0) {
            const subscription = userSubscriptions.find(sub => sub.id === parseInt(subscriptionId));
            if (subscription) {
                setSelectedSubscription(subscription);
                // Clear the subscriptionId parameter from URL
                setSearchParams({});
            }
        }
    }, [searchParams, userSubscriptions]);

    const fetchAvailableOperations = async () => {
        try {
            setLoading(true);
            const response = await axiosWithAuth.get('/external/operations');
            setOperations(response.data.operations || []);
            setQuotaInfo(response.data.quota_info || {});
            setUserRole(response.data.user_role || 'user');
        } catch (error) {
            setError('Failed to fetch available operations: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const fetchUserSubscriptions = async () => {
        try {
            const response = await axiosWithAuth.get('/subscriptions/my-subscriptions');
            const activeSubscriptions = response.data.filter(sub =>
                sub.status === 'active' && !isCASOnlySubscription(sub)
            );
            setUserSubscriptions(activeSubscriptions);
            if (activeSubscriptions.length > 0) {
                setSelectedSubscription(activeSubscriptions[0]);
            }
        } catch (error) {
            console.error('Failed to fetch subscriptions:', error);
        }
    };

    const handleExecuteOperation = async () => {
        try {
            setExecuting(true);

            // Prepare the request data
            const requestData = {
                ...operationForm,
                subscription_id: selectedSubscription?.id
            };

            // Set dump_content_files based on operation type per configuration matrix
            if (['generate_images', 'generate_blogs', 'generate_pages'].includes(selectedOperation.name)) {
                requestData.dump_content_files = true;  // No WordPress site - content/images only
            } else {
                requestData.dump_content_files = false; // WordPress site creation
            }

            // For image-only generation, ensure correct flags
            if (selectedOperation.name === 'generate_images') {
                // Add required flags for image-only generation
                requestData.page_generation_mode = 'custom';
                requestData.blog_generation_mode = 'custom';
                requestData.requested_pages = [];
                requestData.requested_blog_topics = [];
            }

            // Convert image prompts to proper format for any operation that uses images (only if provided)
            if (operationForm.requested_image_prompts && operationForm.requested_image_prompts.trim() && ['generate_images', 'generate_pages', 'generate_blogs', 'create_website'].includes(selectedOperation.name)) {
                const imagePrompts = operationForm.requested_image_prompts
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line && line.includes(':'))
                    .map(line => {
                        const [identifier, ...promptParts] = line.split(':');
                        const prompt = promptParts.join(':').trim();
                        return {
                            identifier: identifier.trim(),
                            prompt: prompt,
                            style: operationForm.image_art_style || 'photorealistic'
                        };
                    });

                if (imagePrompts.length > 0) {
                    requestData.requested_image_prompts = imagePrompts;
                } else {
                    // Fallback: create default identifiers for lines without colons
                    const fallbackPrompts = operationForm.requested_image_prompts
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line)
                        .map((prompt, index) => ({
                            identifier: `image_${index + 1}`,
                            prompt: prompt,
                            style: operationForm.image_art_style || 'photorealistic'
                        }));
                    requestData.requested_image_prompts = fallbackPrompts;
                }
            }

            // Process array fields for proper API format
            if (requestData.requested_blog_topics && typeof requestData.requested_blog_topics === 'string') {
                requestData.requested_blog_topics = requestData.requested_blog_topics
                    .split('\n')
                    .map(topic => topic.trim())
                    .filter(topic => topic);
            }

            if (requestData.key_value_propositions && typeof requestData.key_value_propositions === 'string') {
                requestData.key_value_propositions = requestData.key_value_propositions
                    .split('\n')
                    .map(prop => prop.trim())
                    .filter(prop => prop);
            }

            if (requestData.brand_attributes && typeof requestData.brand_attributes === 'string') {
                requestData.brand_attributes = requestData.brand_attributes
                    .split('\n')
                    .map(attr => attr.trim())
                    .filter(attr => attr);
            }

            if (requestData.seo_primary_keywords && typeof requestData.seo_primary_keywords === 'string') {
                requestData.seo_primary_keywords = requestData.seo_primary_keywords
                    .split('\n')
                    .map(keyword => keyword.trim())
                    .filter(keyword => keyword);
            }

            if (requestData.seo_secondary_keywords && typeof requestData.seo_secondary_keywords === 'string') {
                requestData.seo_secondary_keywords = requestData.seo_secondary_keywords
                    .split('\n')
                    .map(keyword => keyword.trim())
                    .filter(keyword => keyword);
            }

            const response = await axiosWithAuth.post(
                `/external/operations/${selectedOperation.name}`,
                requestData
            );

            setTaskId(response.data.task_id);
            setSuccess(`Operation started successfully! Task ID: ${response.data.task_id}`);
            setOpenDialog(false);

            // Start polling for task status
            if (response.data.task_id) {
                pollTaskStatus(response.data.task_id);
            }

            // Refresh quota info
            fetchAvailableOperations();
        } catch (error) {
            setError('Failed to execute operation: ' + (error.response?.data?.detail || error.message));
        } finally {
            setExecuting(false);
        }
    };

    const pollTaskStatus = async (taskId) => {
        try {
            const response = await axiosWithAuth.get(`/external/task-status/${taskId}`);
            setTaskStatus(response.data);

            // Continue polling if task is still running
            if (response.data.status === 'RUNNING' || response.data.status === 'QUEUED' || response.data.status === 'PENDING' || response.data.status === 'PROGRESS') {
                setTimeout(() => pollTaskStatus(taskId), 5000); // Poll every 5 seconds
            } else {
                // Task completed (SUCCESS, FAILURE, or other terminal states)
                console.log(`Task ${taskId} completed with status: ${response.data.status}`);
            }
        } catch (error) {
            console.error('Failed to get task status:', error);
        }
    };

    const openOperationDialog = (operation) => {
        setSelectedOperation(operation);
        setOperationForm({});
        setTaskId(null);
        setTaskStatus(null);
        setOpenDialog(true);
    };

    const getOperationIcon = (operationName) => {
        switch (operationName) {
            case 'create_website':
                return <WebIcon />;
            case 'generate_content':
                return <CreateIcon />;
            case 'generate_blogs':
                return <ArticleIcon />;
            case 'generate_images':
                return <ImageIcon />;
            default:
                return <CloudIcon />;
        }
    };

    const getOperationColor = (operation) => {
        if (!operation.available) return 'default';
        if (operation.admin_only && userRole === 'user') return 'warning';
        return 'primary';
    };

    const renderOperationForm = () => {
        if (!selectedOperation) return null;

        const renderField = (param) => {
            switch (param) {
                case 'entity_name':
                    return (
                        <TextField
                            fullWidth
                            label="Business/Entity Name"
                            value={operationForm.entity_name || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, entity_name: e.target.value })}
                            required
                        />
                    );
                case 'business_description':
                    return (
                        <TextField
                            fullWidth
                            label="Business Description"
                            multiline
                            rows={3}
                            value={operationForm.business_description || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, business_description: e.target.value })}
                            required
                        />
                    );
                case 'services_products_description':
                    return (
                        <TextField
                            fullWidth
                            label="Services/Products Description"
                            multiline
                            rows={3}
                            value={operationForm.services_products_description || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, services_products_description: e.target.value })}
                            required
                        />
                    );
                case 'target_audience':
                    return (
                        <TextField
                            fullWidth
                            label="Target Audience"
                            value={operationForm.target_audience || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, target_audience: e.target.value })}
                        />
                    );
                case 'requested_blog_topics':
                    return (
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Blog Topics (one per line)"
                            value={operationForm.requested_blog_topics || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, requested_blog_topics: e.target.value })}
                            placeholder="The Future of AI in Manufacturing&#10;Digital Transformation Best Practices&#10;Custom Software Development Process&#10;Industry Trends and Insights"
                            helperText="Enter one blog topic per line for better organization"
                        />
                    );
                case 'requested_image_prompts':
                    return (
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Image Prompts (identifier:prompt format)"
                            value={operationForm.requested_image_prompts || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, requested_image_prompts: e.target.value })}
                            placeholder="hero_banner: Modern tech office with diverse team&#10;about_team: Professional development team collaborating&#10;services_icon: Abstract representation of digital services"
                            helperText="Format: identifier: prompt (one per line). Example: hero_banner: Modern office environment"
                        />
                    );
                case 'image_art_style':
                    return (
                        <FormControl fullWidth>
                            <InputLabel>Image Art Style</InputLabel>
                            <Select
                                value={operationForm.image_art_style || 'photorealistic'}
                                onChange={(e) => setOperationForm({ ...operationForm, image_art_style: e.target.value })}
                                label="Image Art Style"
                            >
                                <MenuItem value="photorealistic">Photorealistic</MenuItem>
                                <MenuItem value="illustration_flat">Illustration Flat</MenuItem>
                                <MenuItem value="illustration_3d_cartoon">Illustration 3D Cartoon</MenuItem>
                                <MenuItem value="abstract_geometric">Abstract Geometric</MenuItem>
                                <MenuItem value="watercolor">Watercolor</MenuItem>
                                <MenuItem value="pixel_art">Pixel Art</MenuItem>
                                <MenuItem value="dark_moody">Dark Moody</MenuItem>
                                <MenuItem value="light_airy">Light Airy</MenuItem>
                            </Select>
                        </FormControl>
                    );
                case 'text_voice':
                    return (
                        <FormControl fullWidth>
                            <InputLabel>Text Voice</InputLabel>
                            <Select
                                value={operationForm.text_voice || 'Third Person (They/The Company)'}
                                onChange={(e) => setOperationForm({ ...operationForm, text_voice: e.target.value })}
                                label="Text Voice"
                            >
                                <MenuItem value="First Person (I/We)">First Person (I/We)</MenuItem>
                                <MenuItem value="Third Person (They/The Company)">Third Person (They/The Company)</MenuItem>
                            </Select>
                        </FormControl>
                    );
                case 'text_formality':
                    return (
                        <FormControl fullWidth>
                            <InputLabel>Text Formality</InputLabel>
                            <Select
                                value={operationForm.text_formality || 'Formal'}
                                onChange={(e) => setOperationForm({ ...operationForm, text_formality: e.target.value })}
                                label="Text Formality"
                            >
                                <MenuItem value="Very Formal">Very Formal</MenuItem>
                                <MenuItem value="Formal">Formal</MenuItem>
                                <MenuItem value="Neutral">Neutral</MenuItem>
                                <MenuItem value="Informal">Informal</MenuItem>
                                <MenuItem value="Very Informal/Conversational">Very Informal/Conversational</MenuItem>
                            </Select>
                        </FormControl>
                    );
                case 'key_value_propositions':
                    return (
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Key Value Propositions (one per line)"
                            value={operationForm.key_value_propositions || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, key_value_propositions: e.target.value })}
                            placeholder="Bespoke AI tailored to your unique challenges&#10;Verifiable ROI through data-driven insights&#10;Partnership with leading research institutions"
                            helperText="List your main competitive advantages or unique selling points"
                        />
                    );
                case 'brand_attributes':
                    return (
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Brand Attributes (one per line)"
                            value={operationForm.brand_attributes || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, brand_attributes: e.target.value })}
                            placeholder="Innovative&#10;Reliable&#10;Professional&#10;Transformative"
                            helperText="Adjectives that describe your brand personality"
                        />
                    );
                case 'seo_primary_keywords':
                    return (
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Primary SEO Keywords (one per line)"
                            value={operationForm.seo_primary_keywords || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, seo_primary_keywords: e.target.value })}
                            placeholder="AI consulting&#10;machine learning&#10;predictive analytics"
                            helperText="Main keywords to target for SEO"
                        />
                    );
                case 'seo_secondary_keywords':
                    return (
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Secondary SEO Keywords (one per line)"
                            value={operationForm.seo_secondary_keywords || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, seo_secondary_keywords: e.target.value })}
                            placeholder="NLP solutions&#10;computer vision&#10;business intelligence"
                            helperText="Supporting keywords for SEO"
                        />
                    );
                case 'primary_call_to_action':
                    return (
                        <TextField
                            fullWidth
                            label="Primary Call to Action"
                            value={operationForm.primary_call_to_action || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, primary_call_to_action: e.target.value })}
                            placeholder="Schedule a Discovery Call"
                            helperText="Main action you want visitors to take"
                        />
                    );
                case 'contact_info':
                    return (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={operationForm.contact_info?.email || ''}
                                    onChange={(e) => setOperationForm({
                                        ...operationForm,
                                        contact_info: {
                                            ...operationForm.contact_info,
                                            email: e.target.value
                                        }
                                    })}
                                    required
                                    helperText="Required for WordPress admin access"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    value={operationForm.contact_info?.phone || ''}
                                    onChange={(e) => setOperationForm({
                                        ...operationForm,
                                        contact_info: {
                                            ...operationForm.contact_info,
                                            phone: e.target.value
                                        }
                                    })}
                                    placeholder="+1-555-0123"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    value={operationForm.contact_info?.address || ''}
                                    onChange={(e) => setOperationForm({
                                        ...operationForm,
                                        contact_info: {
                                            ...operationForm.contact_info,
                                            address: e.target.value
                                        }
                                    })}
                                    placeholder="123 Main St, City, State"
                                />
                            </Grid>
                        </Grid>
                    );
                case 'website_style_preference':
                    return (
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Website Style Preference"
                            value={operationForm.website_style_preference || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, website_style_preference: e.target.value })}
                            placeholder="Modern, clean, and professional with a focus on usability"
                            helperText="Describe the overall look and feel you want"
                        />
                    );
                default:
                    return (
                        <TextField
                            fullWidth
                            label={param.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            value={operationForm[param] || ''}
                            onChange={(e) => setOperationForm({ ...operationForm, [param]: e.target.value })}
                        />
                    );
            }
        };

        return (
            <Grid container spacing={2}>
                {selectedOperation.parameters?.map((param) => (
                    <Grid item xs={12} key={param}>
                        {renderField(param)}
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderTaskStatus = () => {
        if (!taskStatus) return null;

        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Task Status
                </Typography>
                <Card>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Chip
                                label={taskStatus.status}
                                color={
                                    taskStatus.status === 'COMPLETED' ? 'success' :
                                        taskStatus.status === 'FAILED' ? 'error' :
                                            'warning'
                                }
                                sx={{ mr: 2 }}
                            />
                            <Typography variant="body2">
                                Task ID: {taskId}
                            </Typography>
                        </Box>

                        {taskStatus.status === 'RUNNING' && (
                            <LinearProgress sx={{ mb: 2 }} />
                        )}

                        {taskStatus.message && (
                            <Typography variant="body2" color="textSecondary">
                                {taskStatus.message}
                            </Typography>
                        )}

                        {taskStatus.result_url && (
                            <Button
                                variant="contained"
                                href={taskStatus.result_url}
                                target="_blank"
                                sx={{ mt: 1 }}
                            >
                                View Results
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </Box>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" gutterBottom>
                AWC Service
            </Typography>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant={isMobile ? "scrollable" : "standard"}
                    scrollButtons={isMobile ? "auto" : false}
                    allowScrollButtonsMobile
                    sx={{
                        '& .MuiTab-root': {
                            minWidth: { xs: 'auto', sm: 'auto' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            padding: { xs: '6px 8px', sm: '12px 16px' }
                        }
                    }}
                >
                    <Tab label={isMobile ? "Generation" : "Content Generation"} />
                    <Tab label={isMobile ? "Status" : "Content Status & Download"} />
                </Tabs>
            </Paper>

            {activeTab === 0 && <ContentGenerator />}
            {activeTab === 1 && <ContentStatusAndDownload userSubscriptions={userSubscriptions} />}
        </Box>
    );
};

export default ServiceOperationsPage; 